import { useState } from 'react'
import { toast } from 'react-toastify'
import to from 'await-to-js'

import { Modal } from 'components/UI/Modal/Modal'
import FbButton from 'components/FbUI/FbButton'

import {
  AIGeneratedSequence,
  useGenerateAISequence,
  useGetAISuggestedPrompts,
} from '../sequences_api'

interface AISequencePromptModalProps {
  campaignId: number
  isOpen: boolean
  onClose: () => void
  onGenerated: (sequence: AIGeneratedSequence) => void
}

export function AISequencePromptModal({
  campaignId,
  isOpen,
  onClose,
  onGenerated,
}: AISequencePromptModalProps) {
  const [prompt, setPrompt] = useState('')
  const generateMutation = useGenerateAISequence()
  const { data: suggestedPrompts } = useGetAISuggestedPrompts()

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe your sequence')
      return
    }

    const [err, result] = await to(
      generateMutation.mutateAsync({
        campaign_id: campaignId,
        prompt: prompt.trim(),
      })
    )

    if (err) {
      toast.error(
        'detail' in (err as Error & { detail?: string })
          ? (err as Error & { detail: string }).detail
          : 'Failed to generate sequence. Please try again.'
      )
      return
    }

    if (result) {
      onGenerated(result)
      setPrompt('')
      onClose()
    }
  }

  const handleSuggestedPrompt = (
    e: React.MouseEvent,
    suggestedPrompt: string
  ) => {
    e.preventDefault()
    e.stopPropagation()
    setPrompt(suggestedPrompt)
  }

  const handleClose = () => {
    setPrompt('')
    onClose()
  }

  return (
    <Modal
      open={isOpen}
      title="Create Sequence with AI"
      description="Describe the email sequence you want to create and AI will generate it for you."
      onOpenChange={(open) => !open && handleClose()}
      loading={generateMutation.isPending}
      blockAccept={!prompt.trim() || generateMutation.isPending}
      onAccept={handleGenerate}
      acceptButtonText={
        generateMutation.isPending ? 'Generating...' : 'Generate Sequence'
      }
      size="lg"
    >
      <div className="space-y-6">
        {/* Suggested Prompts */}
        {suggestedPrompts && suggestedPrompts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Start
            </label>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((suggestion) => (
                <FbButton
                  key={suggestion.label}
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={(e) => handleSuggestedPrompt(e, suggestion.prompt)}
                  title={suggestion.description}
                >
                  {suggestion.label}
                </FbButton>
              ))}
            </div>
          </div>
        )}

        {/* Prompt Input */}
        <div>
          <label
            htmlFor="ai-prompt"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Describe your sequence
          </label>
          <textarea
            id="ai-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="E.g., Create a 3-email cold outreach sequence introducing our organic snacks to school food directors. Focus on health benefits and easy ordering."
            className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
            disabled={generateMutation.isPending}
          />
        </div>

        {/* Error state */}
        {generateMutation.isError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">
              Failed to generate sequence. Please try a different prompt or try
              again later.
            </p>
          </div>
        )}

        {/* Loading indicator */}
        {generateMutation.isPending && (
          <div className="flex items-center justify-center p-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm text-gray-600">
                AI is crafting your sequence...
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
